import React, { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { useCCPOPRPSievesContext, CCP_SCREENS } from '../context/CCPOPRPSievesContext';
import type { SievesMagnetsItemRow, CompletedCycleRecord } from '../context/CCPOPRPSievesContext';
import { ccpOprpSievesService } from '../services/ccpOprpSievesService';
import { getSavedPhotoNames, saveCyclePhotos } from '../services/ccpPhotoStore';
import { SIEVES_MAGNETS_ITEMS } from '../constants/ccpOprpSievesData';
import { SP_MSAL_SCOPE } from '../../../../constants/sharepoint';
import PhotoAttachField from './PhotoAttachField';
import type { AttachedPhoto } from './PhotoAttachField';
import moment from 'moment';

const SievesMagnetsChecklist: React.FC = () => {
    const { state, dispatch } = useCCPOPRPSievesContext();
    const { instance, accounts } = useMsal();
    const session = state.sessionData;

    const [currentCycle, setCurrentCycle] = useState(state.currentCycle || "Cycle-1");
    // const [selectedPlantGroup, ] = useState<'All' | 'New Plant' | 'Old Plant'>('All');
    const [selectedFrequency] = useState<'4hrs' | '8hrs'>(state.selectedFrequency || '8hrs');
    const [items, setItems] = useState<SievesMagnetsItemRow[]>([]);
    const [executiveName, setExecutiveName] = useState(session?.productionIncharge || "Production Incharge (Rajpura)");
    const [isSessionStarted, setIsSessionStarted] = useState(false);

    useEffect(() => {
        if (session?.productionIncharge) {
            setExecutiveName(session.productionIncharge);
        }
    }, [session]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [expandedCycle, setExpandedCycle] = useState<string | null>(null);
    const [photos, setPhotos] = useState<AttachedPhoto[]>([]);

    // Shift modal
    const [isShiftModalOpen, setIsShiftModalOpen] = useState(false);
    const [selectedShiftModal, setSelectedShiftModal] = useState(session?.shift || state.selectedShift || "Shift-1");

    // Dynamic deviation inputs in completed cycles
    const [actionPlans, setActionPlans] = useState<Record<string, string>>({});
    const [qaComments, setQaComments] = useState<Record<string, string>>({});

    // Load all completed cycles from Dataverse/offline if not loaded or tour changes
    useEffect(() => {
        if (!state.currentTourId) return;

        const loadCycles = async () => {
            try {
                const fetchedItems = await ccpOprpSievesService.getSievesMagnetsCheckpoints(state.currentTourId!);
                if (fetchedItems && fetchedItems.length > 0) {
                    const cycleGroups: Record<string, any[]> = {};
                    fetchedItems.forEach((si: any) => {
                        const cName = si.cr3ea_cycle || "Cycle-1";
                        if (!cycleGroups[cName]) cycleGroups[cName] = [];
                        cycleGroups[cName].push(si);
                    });

                    const sortedCycleKeys = Object.keys(cycleGroups).sort((a, b) => {
                        const numA = parseInt(a.replace(/\D/g, ""), 10) || 0;
                        const numB = parseInt(b.replace(/\D/g, ""), 10) || 0;
                        return numA - numB;
                    });

                    const completedRecords: CompletedCycleRecord[] = sortedCycleKeys.map(cName => {
                        const rows = cycleGroups[cName];
                        const first = rows[0];
                        const formattedRows: SievesMagnetsItemRow[] = rows.map((r: any, idx: number) => {
                            const desc = r.cr3ea_description || "";
                            const isNewPlant = desc.includes("Line 5") || desc.includes("L-5") || desc.includes("Line 6") || desc.includes("L-6") || desc.includes("L-5&7");
                            const plantGroup: "New Plant" | "Old Plant" = isNewPlant ? "New Plant" : "Old Plant";

                            return {
                                id: r.cr3ea_prod_rajpura_sievesmagnetsid || `resumed_${idx}`,
                                description: desc,
                                frequency: (r.cr3ea_frequency === "4hrs" ? "4hrs" : "8hrs") as "4hrs" | "8hrs",
                                plantGroup,
                                criteria: r.cr3ea_criteria || "Okay",
                                defectRemarks: r.cr3ea_defectremarks
                            };
                        });

                        const total = formattedRows.length;
                        const ok = formattedRows.filter(f => f.criteria === "Okay").length;
                        const score = total > 0 ? Math.round((ok / total) * 100) : 100;

                        return {
                            cycle: cName,
                            cycleDate: first?.createdon || new Date().toISOString(),
                            sievesItems: formattedRows,
                            score,
                            hasDeviations: formattedRows.some(f => f.criteria === "Not Okay"),
                            status: "Submitted"
                        };
                    });

                    const savedPhotos = await getSavedPhotoNames(state.currentTourId!, 'SIEVES');
                    completedRecords.forEach(record => {
                        if (!record.photoFileNames?.length && savedPhotos[record.cycle]) {
                            record.photoFileNames = savedPhotos[record.cycle];
                        }
                    });

                    dispatch({ type: 'SET_COMPLETED_CYCLES', payload: completedRecords });
                    const maxCycleNum = sortedCycleKeys.reduce((max, k) => {
                        const num = parseInt(k.replace(/\D/g, ""), 10) || 0;
                        return num > max ? num : max;
                    }, 0);
                    const nextCycleNumber = maxCycleNum + 1;
                    setCurrentCycle(`Cycle-${nextCycleNumber}`);
                    dispatch({ type: 'SET_CURRENT_CYCLE', payload: `Cycle-${nextCycleNumber}` });
                }
            } catch (err) {
                console.warn("Could not load Sieves cycle history:", err);
            }
        };

        loadCycles();
    }, [state.currentTourId]);

    // Initialize items based on line/section (plant group) and frequency
    useEffect(() => {
        const lineVal = String(session?.line || "").trim();
        let targetPlantGroup: "New Plant" | "Old Plant" = "New Plant";
        
        if (lineVal.includes("1,2,3,4,8") || lineVal.includes("1, 2, 3, 4, 8") || lineVal === "OldPlant" || lineVal === "Line-1" || lineVal === "Line-2" || lineVal === "Line-3" || lineVal === "Line-4" || lineVal === "Line-8" || lineVal.includes("1,2,3,4")) {
            targetPlantGroup = "Old Plant";
        } else if (lineVal.includes("5,6,7") || lineVal.includes("5, 6, 7") || lineVal === "NewPlant" || lineVal === "Line-5" || lineVal === "Line-6" || lineVal === "Line-7") {
            targetPlantGroup = "New Plant";
        }

        const filteredTemplates = SIEVES_MAGNETS_ITEMS.filter(item => 
            item.frequency === selectedFrequency && item.plantGroup === targetPlantGroup
        );
        
        const rows: SievesMagnetsItemRow[] = filteredTemplates.map(t => ({
            id: t.id,
            description: t.description,
            frequency: t.frequency,
            plantGroup: t.plantGroup,
            criteria: "Okay",
            defectRemarks: ""
        }));
        setItems(rows);
        setIsSessionStarted(false);
        setPhotos(prev => {
            prev.forEach(photo => URL.revokeObjectURL(photo.previewUrl));
            return [];
        });
    }, [selectedFrequency, session?.line, currentCycle]);

    const handleCriteriaChange = (index: number, criteria: "Okay" | "Not Okay") => {
        setItems(prev => {
            const updated = [...prev];
            const item = { ...updated[index] };
            item.criteria = criteria;
            if (criteria === "Okay") {
                item.defectRemarks = "";
            }
            updated[index] = item;
            return updated;
        });
    };

    const handleRemarksChange = (index: number, remarks: string) => {
        setItems(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], defectRemarks: remarks };
            return updated;
        });
    };

    const getNextCycleName = (cycleStr: string) => {
        const match = cycleStr.match(/\d+/);
        const num = match ? parseInt(match[0], 10) : 1;
        return `Cycle-${num + 1}`;
    };

    // Save or Pause Session
    const handleSaveSession = async (isPause: boolean = false) => {
        if (!state.currentTourId || !session) {
            alert("No active session found.");
            return;
        }

        if (!isPause) {
            const missingRemarks = items.find(item => item.criteria === "Not Okay" && !item.defectRemarks?.trim());
            if (missingRemarks) {
                alert(`Please provide Defect Remarks for: ${missingRemarks.description}`);
                return;
            }
        }

        setIsSubmitting(true);
        try {
            let photoFileNames: string[] = [];
            if (photos.length > 0) {
                let accessToken: string | undefined;
                try {
                    if (accounts[0]) {
                        const tokenResponse = await instance.acquireTokenSilent({
                            scopes: [SP_MSAL_SCOPE],
                            account: accounts[0],
                        });
                        accessToken = tokenResponse.accessToken;
                    }
                } catch (tokenError) {
                    console.warn('SharePoint token unavailable for Sieves photos:', tokenError);
                }
                photoFileNames = await saveCyclePhotos({
                    tourId: state.currentTourId,
                    cycle: currentCycle,
                    section: 'SIEVES',
                    files: photos.map(photo => photo.file),
                    accessToken,
                });
            }

            await ccpOprpSievesService.cleanChecklistItems(state.currentTourId, "SIEVES", currentCycle);
            await ccpOprpSievesService.saveSievesMagnetsCycle(
                state.currentTourId,
                currentCycle,
                items,
                { ...session, productionIncharge: executiveName },
                selectedFrequency
            );

            const totalItems = items.length;
            const okItems = items.filter(c => c.criteria === "Okay").length;
            const score = totalItems > 0 ? Math.round((okItems / totalItems) * 100) : 100;
            const hasDeviations = items.some(c => c.criteria === "Not Okay");

            let status: CompletedCycleRecord['status'] = "Completed";
            if (isPause) {
                status = "Checklist Filling - Paused";
            } else if (hasDeviations) {
                status = "Pending Production Action";
            }

            const completedRecord: CompletedCycleRecord = {
                cycle: currentCycle,
                cycleDate: new Date().toISOString(),
                sessionTime: moment().format("HH:mm"),
                productName: "Sieves & Magnets",
                executiveName,
                location: "Sieves & Magnets Area",
                acceptanceResponse: "OK",
                sievesItems: [...items],
                score,
                hasDeviations,
                photoFileNames,
                status,
                isPaused: isPause
            };

            dispatch({ type: 'ADD_COMPLETED_CYCLE', payload: completedRecord });

            if (!isPause) {
                const nextCycle = getNextCycleName(currentCycle);
                setCurrentCycle(nextCycle);
                dispatch({ type: 'SET_CURRENT_CYCLE', payload: nextCycle });
                setIsSessionStarted(false);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                alert("Checklist progress paused and saved locally.");
            }
        } catch (err) {
            console.error("Error saving Sieves cycle:", err);
            alert("Failed to save Sieves & Magnets checkpoints.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Corrective Action Submission
    const handleSubmitCorrectiveActions = async (cycleName: string, cycleRecord: CompletedCycleRecord) => {
        if (!state.currentTourId) return;

        const updatedItems = (cycleRecord.sievesItems || []).map(si => {
            const key = `${cycleName}_${si.id}`;
            const action = actionPlans[key] || si.actionTaken || "Action implemented";
            if (si.criteria === "Not Okay" && si.deviationStatus !== "Closed") {
                return {
                    ...si,
                    actionTaken: action,
                    deviationStatus: "Action Taken" as const
                };
            }
            return si;
        });

        setIsSubmitting(true);
        try {
            for (const si of updatedItems) {
                if (si.criteria === "Not Okay" && si.dbId) {
                    await ccpOprpSievesService.updateDeviationStatus(si.dbId, "SIEVES", {
                        cr3ea_actiontaken: si.actionTaken,
                        cr3ea_deviationstatus: "Action Taken"
                    });
                }
            }

            dispatch({
                type: 'UPDATE_COMPLETED_CYCLE',
                payload: {
                    cycle: cycleName,
                    record: {
                        sievesItems: updatedItems,
                        status: "Pending QA Re-Verification"
                    }
                }
            });
            alert("Corrective actions submitted to QA.");
        } catch (err) {
            console.error("Failed to submit corrective action:", err);
            alert("Failed to update corrective action.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // QA Closure
    const handleQAClosure = async (cycleName: string, cycleRecord: CompletedCycleRecord, isApprove: boolean) => {
        if (!state.currentTourId) return;

        const updatedItems = (cycleRecord.sievesItems || []).map(si => {
            const key = `${cycleName}_${si.id}`;
            const comment = qaComments[key] || (isApprove ? "Verified and closed by QA" : "Rejected by QA");
            if (si.criteria === "Not Okay") {
                if (isApprove) {
                    return {
                        ...si,
                        defectRemarks: `${si.defectRemarks || ''} | Re-verified: ${comment}`,
                        deviationStatus: "Closed" as const
                    };
                } else {
                    return {
                        ...si,
                        deviationStatus: "New" as const
                    };
                }
            }
            return si;
        });

        setIsSubmitting(true);
        try {
            for (const si of updatedItems) {
                if (si.criteria === "Not Okay" && si.dbId) {
                    await ccpOprpSievesService.updateDeviationStatus(si.dbId, "SIEVES", {
                        cr3ea_deviationstatus: isApprove ? "Closed" : "New",
                        cr3ea_defectremarks: si.defectRemarks
                    });
                }
            }

            const allResolved = !updatedItems.some(si => si.criteria === "Not Okay" && si.deviationStatus !== "Closed");

            dispatch({
                type: 'UPDATE_COMPLETED_CYCLE',
                payload: {
                    cycle: cycleName,
                    record: {
                        sievesItems: updatedItems,
                        status: isApprove && allResolved ? "Completed" : "Pending Production Action"
                    }
                }
            });
            alert(isApprove ? "Cycle verified and closed successfully." : "Actions rejected. Production notified to resubmit.");
        } catch (err) {
            console.error("QA Closure update failed:", err);
            alert("Failed to update QA verification.");
        } finally {
            setIsSubmitting(false);
        }
    };

    // Stepper component
    const renderStepper = (status: string) => {
        const steps = [
            { id: 1, label: "Setup Info" },
            { id: 2, label: "Checklist Fill" },
            { id: 3, label: "Action Plan" },
            { id: 4, label: "Re-Verify" },
            { id: 5, label: "Closed" }
        ];

        let activeIdx = 0;
        if (status === "Metadata Setup") activeIdx = 0;
        else if (status === "Checklist Filling" || status === "Checklist Filling - Paused") activeIdx = 1;
        else if (status === "Pending Production Action") activeIdx = 2;
        else if (status === "Pending QA Re-Verification") activeIdx = 3;
        else if (status === "Completed" || status === "Not Operational" || status === "Escalated") activeIdx = 4;

        return (
            <div className="tour-workflow-stepper">
                {steps.map((step, idx) => {
                    const isCompleted = idx < activeIdx;
                    const isActive = idx === activeIdx;

                    return (
                        <React.Fragment key={step.id}>
                            {isCompleted ? (
                                <div className="step-item completed">
                                    <span style={{ fontWeight: 'bold' }}>✓</span>
                                    <span>{step.label}</span>
                                </div>
                            ) : isActive ? (
                                <div className="step-item active">
                                    <span>{step.id}.</span>
                                    <span>{step.label}</span>
                                </div>
                            ) : (
                                <div className="step-item upcoming">
                                    <span>{step.id}.</span>
                                    <span>{step.label}</span>
                                </div>
                            )}
                            {idx < steps.length - 1 && (
                                <div className="step-divider">➔</div>
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    const handleShiftChange = () => {
        dispatch({
            type: 'SET_SELECTIONS',
            payload: {
                parameterVerification: "Sieves and Magnets",
                shift: selectedShiftModal,
                frequency: state.selectedFrequency
            }
        });
        if (session) {
            dispatch({
                type: 'SET_SESSION_DATA',
                payload: { ...session, shift: selectedShiftModal }
            });
        }
        setIsShiftModalOpen(false);
    };

    // const displayedItems = items.filter(it => {
    //     if (selectedPlantGroup === 'All') return true;
    //     return it.plantGroup === selectedPlantGroup;
    // });

    return (
        <div id="rajpuraQualityDashboard">
            <main className="plan-tour-content">
                <div id="checklist-main-container">
                    {/* Header */}
                    <div className="plan-tour-header">
                        <div className="tour-header-info-wrapper">
                            <h3 className="tour-header-title">CCP, OPRP & Sieves/Magnets (Rajpura)</h3>
                            <div className="tour-header-meta">
                                <span className="badge" id="shiftBadge">{session?.shift || state.selectedShift || "Shift 1"}</span>
                                <span className="tour-date" id="currentDay">{moment().format("DD/MM/YYYY")}</span>
                            </div>
                            <div className="bs-menu-parent">
                                <button
                                    type="button"
                                    className="bs-btn icon-btn bs-menu-opener"
                                    onClick={() => setIsShiftModalOpen(true)}
                                    title="Change Shift"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
                                        <path d="M7.56043 9.00004C7.56043 9.79533 8.20514 10.44 9.00043 10.44C9.79572 10.44 10.4404 9.79533 10.4404 9.00004C10.4404 8.20475 9.79572 7.56004 9.00043 7.56004C8.20514 7.56004 7.56043 8.20475 7.56043 9.00004Z" fill="#0C0D10" />
                                        <path d="M7.56043 13.59C7.56043 14.3853 8.20514 15.03 9.00043 15.03C9.79572 15.03 10.4404 14.3853 10.4404 13.59C10.4404 12.7947 9.79572 12.15 9.00043 12.15C8.20514 12.15 7.56043 12.7947 7.56043 13.59Z" fill="#0C0D10" />
                                        <path d="M7.56043 4.41002C7.56043 5.20531 8.20514 5.85002 9.00043 5.85002C9.79572 5.85002 10.4404 5.20531 10.4404 4.41002C10.4404 3.61473 9.79572 2.97002 9.00043 2.97002C8.20514 2.97002 7.56043 3.61473 7.56043 4.41002Z" fill="#0C0D10" />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Shift Modal */}
                    {isShiftModalOpen && (
                        <div className="main-popup is-popup-active">
                            <div className="popup-bg" onClick={() => setIsShiftModalOpen(false)}></div>
                            <div className="popup-content">
                                <div className="popup-common-box">
                                    <div className="popup-header">
                                        <h4 className="popup-header-title">Select your shift</h4>
                                        <button type="button" className="bs-btn popup-close-btn" onClick={() => setIsShiftModalOpen(false)}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                <path d="M11 1L1 11M1 1L11 11" stroke="#6D757E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                    <div className="popup-body">
                                        <label className="form-label">Shift</label>
                                        <select
                                            className="form-select"
                                            value={selectedShiftModal}
                                            onChange={e => setSelectedShiftModal(e.target.value)}
                                        >
                                            <option value="Shift-1">Shift 1</option>
                                            <option value="Shift-2">Shift 2</option>
                                            <option value="Shift-3">Shift 3</option>
                                        </select>
                                    </div>
                                    <div className="popup-footer">
                                        <button type="button" className="bs-btn bs-btn-outline-primary" onClick={() => setIsShiftModalOpen(false)}>
                                            Cancel
                                        </button>
                                        <button type="button" className="bs-btn bs-btn-primary" onClick={handleShiftChange}>
                                            Done
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Cycles List */}
                    <div className="plan-tour-body">
                        <div className="tour-cycle-card-panel-lists">
                            {/* Completed Cycles History */}
                            {state.completedCycles.map((rec, cIdx) => {
                                const isExpanded = expandedCycle === rec.cycle;
                                const status = rec.status || (rec.hasDeviations ? "Pending Production Action" : "Completed");
                                const deviations = (rec.sievesItems || []).filter(si => si.criteria === "Not Okay");

                                let badgeColor = "#64748b";
                                let badgeBg = "#f1f5f9";
                                if (status === "Completed") { badgeColor = "#16a34a"; badgeBg = "#dcfce7"; }
                                else if (status === "Pending Production Action") { badgeColor = "#ea580c"; badgeBg = "#ffedd5"; }
                                else if (status === "Pending QA Re-Verification") { badgeColor = "#7c3aed"; badgeBg = "#ede9fe"; }
                                else if (status === "Escalated" || status === "Not Operational") { badgeColor = "#dc2626"; badgeBg = "#fee2e2"; }

                                return (
                                    <div
                                        key={cIdx}
                                        className={`bs-card bs-card-secondary tour-cycle-panel ${isExpanded ? 'bs-card-toggler-is-active' : ''}`}
                                    >
                                        <div
                                            className="bs-card-header"
                                            onClick={() => setExpandedCycle(isExpanded ? null : rec.cycle)}
                                        >
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <h4 className="bs-card-title" style={{ margin: 0 }}>{rec.cycle}</h4>
                                                <span
                                                    className="badge"
                                                    style={{ backgroundColor: badgeBg, color: badgeColor, fontWeight: 600, padding: '4px 8px', borderRadius: 4, fontSize: 11 }}
                                                >
                                                    {status}
                                                </span>
                                                {!!rec.photoFileNames?.length && (
                                                    <span style={{ fontSize: 12, color: '#2563eb', fontWeight: 600 }}>
                                                        {rec.photoFileNames.length} photo{rec.photoFileNames.length === 1 ? '' : 's'}
                                                    </span>
                                                )}
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                                <span className="tour-date" style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                                                    {rec.cycleDate ? moment(rec.cycleDate).format("DD/MM/YYYY") : moment().format("DD/MM/YYYY")}
                                                </span>
                                                <button type="button" className="bs-btn icon-btn bs-card-toggler-btn">
                                                    <svg
                                                        style={{ transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}
                                                        xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"
                                                    >
                                                        <path d="M3.66667 5.66666L8.33333 10.3333L13 5.66666" stroke="#0C0D10" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="bs-card-body">
                                                {renderStepper(status)}

                                                <div className="tour-cycle-info-wrapper" style={{ marginBottom: 15 }}>
                                                    <div className="tour-cyle-start-info">
                                                        <div><strong>Executive:</strong> {rec.executiveName || "N/A"}</div>
                                                        <div><strong>Frequency:</strong> {selectedFrequency}</div>
                                                        <div><strong>Score:</strong> {rec.score || 100}%</div>
                                                        <div><strong>Response:</strong> {rec.acceptanceResponse || "OK"}</div>
                                                    </div>
                                                </div>

                                                <div className="tour-cyle-step-completed">
                                                    <h5 style={{ marginTop: 0, fontWeight: 'bold', color: '#1e293b' }}>
                                                        Sieves & Magnets Checklist Summary
                                                    </h5>
                                                    <div className="bs-table-container">
                                                        <table className="bs-table">
                                                            <thead>
                                                                <tr>
                                                                    <th>Sieve / Magnet Description</th>
                                                                    <th style={{ textAlign: 'center' }}>Status</th>
                                                                    <th>Defect Remarks</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {(rec.sievesItems || []).map((si, i) => {
                                                                    const isClosed = si.deviationStatus === "Closed";
                                                                    const displayCriteria = isClosed ? "Okay" : (si.criteria || "Okay");
                                                                    return (
                                                                        <tr key={i}>
                                                                            <td style={{ fontWeight: 600 }}>{si.description}</td>
                                                                            <td style={{ textAlign: 'center' }} className={displayCriteria === 'Okay' ? 'ok-text' : 'not-ok-text'}>
                                                                                {displayCriteria}
                                                                            </td>
                                                                            <td>{si.defectRemarks || "-"}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </div>

                                                {/* Deviation Workflow Panel */}
                                                {deviations.length > 0 && status !== "Completed" && (
                                                    <div className="deviation-workflow-panel">
                                                        <h5 style={{ margin: '0 0 12px 0', fontWeight: 'bold', color: '#c2410c' }}>
                                                            ⚠️ Quality Deviation Registered
                                                        </h5>

                                                        {deviations.map((dev, dIdx) => {
                                                            const devKey = `${rec.cycle}_${dev.id}`;
                                                            const isClosed = dev.deviationStatus === "Closed";

                                                            return (
                                                                <div key={dIdx} className="deviation-item">
                                                                    <div style={{ fontWeight: 'bold', color: '#1e293b', fontSize: 13, marginBottom: 4 }}>
                                                                        {dev.description}
                                                                    </div>
                                                                    <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', padding: 8, borderRadius: 6, marginBottom: 8 }}>
                                                                        <strong>QA Remarks:</strong> {dev.defectRemarks || "Deviation recorded"}
                                                                    </div>

                                                                    {status === "Pending Production Action" && (
                                                                        <div style={{ marginTop: 8 }}>
                                                                            <label className="form-label" style={{ fontSize: 12 }}>
                                                                                Corrective Action Plan (Production)
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                placeholder="Describe corrective actions taken..."
                                                                                value={actionPlans[devKey] || ""}
                                                                                onChange={e => setActionPlans(prev => ({ ...prev, [devKey]: e.target.value }))}
                                                                            />
                                                                        </div>
                                                                    )}

                                                                    {status === "Pending QA Re-Verification" && (
                                                                        <div style={{ marginTop: 8 }}>
                                                                            <div style={{ fontSize: 12, color: '#334155', marginBottom: 6 }}>
                                                                                <strong>Production Action:</strong> {dev.actionTaken || "Action taken"}
                                                                            </div>
                                                                            <label className="form-label" style={{ fontSize: 12 }}>
                                                                                QA Re-verification Comment
                                                                            </label>
                                                                            <input
                                                                                type="text"
                                                                                className="form-control"
                                                                                placeholder="Enter QA verification remarks..."
                                                                                value={qaComments[devKey] || ""}
                                                                                onChange={e => setQaComments(prev => ({ ...prev, [devKey]: e.target.value }))}
                                                                            />
                                                                        </div>
                                                                    )}

                                                                    {isClosed && (
                                                                        <div style={{ fontSize: 12, color: '#16a34a', fontWeight: 600 }}>
                                                                            ✓ Resolved & Closed
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}

                                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 12 }}>
                                                            {status === "Pending Production Action" && (
                                                                <button
                                                                    type="button"
                                                                    disabled={isSubmitting}
                                                                    onClick={() => handleSubmitCorrectiveActions(rec.cycle, rec)}
                                                                    className="bs-btn bs-btn-primary"
                                                                >
                                                                    Submit Corrective Actions
                                                                </button>
                                                            )}

                                                            {status === "Pending QA Re-Verification" && (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        disabled={isSubmitting}
                                                                        onClick={() => handleQAClosure(rec.cycle, rec, false)}
                                                                        className="bs-btn bs-btn-outline-primary"
                                                                    >
                                                                        Reject Actions
                                                                    </button>
                                                                    <button
                                                                        type="button"
                                                                        disabled={isSubmitting}
                                                                        onClick={() => handleQAClosure(rec.cycle, rec, true)}
                                                                        className="bs-btn bs-btn-primary"
                                                                        style={{ backgroundColor: '#16a34a', borderColor: '#16a34a' }}
                                                                    >
                                                                        Approve & Close Cycle
                                                                    </button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}

                            {/* Active Cycle Card */}
                            <div className="bs-card bs-card-secondary tour-cycle-panel bs-card-toggler-is-active" id={`cycle-${currentCycle}`}>
                                <div className="bs-card-header">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                        <h4 className="bs-card-title" style={{ margin: 0 }}>{currentCycle}</h4>
                                        <span
                                            className="badge"
                                            style={{
                                                backgroundColor: isSessionStarted ? '#fef3c7' : '#dbeafe',
                                                color: isSessionStarted ? '#d97706' : '#2563eb',
                                                fontWeight: 600,
                                                padding: '4px 8px',
                                                borderRadius: 4,
                                                fontSize: 11
                                            }}
                                        >
                                            {isSessionStarted ? "Checklist Filling" : "Metadata Setup"}
                                        </span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span className="tour-date" style={{ fontSize: 13, color: '#64748b', fontWeight: 500 }}>
                                            {moment().format("DD/MM/YYYY")}
                                        </span>
                                        <button type="button" className="bs-btn icon-btn bs-card-toggler-btn">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                                                <path d="M3.66667 5.66666L8.33333 10.3333L13 5.66666" stroke="#0C0D10" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>

                                <div className="bs-card-body" style={{ display: 'block' }}>
                                    {renderStepper(isSessionStarted ? "Checklist Filling" : "Metadata Setup")}

                                    {!isSessionStarted ? (
                                        <div className="tour-cyle-step-start">
                                            <div className="form-group" style={{ marginBottom: 12 }}>
                                                <label className="form-label">Production Executive</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={executiveName}
                                                    onChange={e => setExecutiveName(e.target.value)}
                                                    placeholder="Enter Production Executive Name..."
                                                />
                                            </div>
                                            <div className="form-group" style={{ marginBottom: 12 }}>
                                                <label className="form-label">Frequency</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={selectedFrequency === "4hrs" ? "4-Hour Check" : "Once a Shift (8-Hour Check)"}
                                                    disabled
                                                />
                                            </div>
                                            <div style={{ marginTop: 15 }}>
                                                <button
                                                    type="button"
                                                    className="bs-btn bs-btn-primary"
                                                    onClick={() => {
                                                        if (!executiveName.trim()) {
                                                            alert("Please fill in the Production Executive field.");
                                                            return;
                                                        }
                                                        setIsSessionStarted(true);
                                                    }}
                                                >
                                                    Start Session
                                                </button>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="tour-cyle-step-form">
                                            {/* Top Metadata Info Bar */}
                                            <div className="tour-cycle-info-wrapper" style={{ marginBottom: 15 }}>
                                                <div className="tour-cyle-start-info" style={{ display: 'flex', flexWrap: 'wrap', gap: 15, background: '#e2e8f0', padding: 12, borderRadius: 8 }}>
                                                    <div><strong>Executive Name:</strong> {executiveName}</div>
                                                    <div><strong>Frequency:</strong> {selectedFrequency === "4hrs" ? "4-Hour Check" : "Once a Shift (8-Hour Check)"}</div>
                                                    <div><strong>Response:</strong> OK</div>
                                                </div>
                                            </div>

                                            {/* Sieve Item Cards */}
                                            <div className="tab-pane-content" style={{ display: 'block' }}>
                                                <div style={{ background: '#f8fafc', padding: 15, borderRadius: 8, border: '1px solid #e2e8f0' }}>
                                                    {items.map(item => {
                                                        const realIdx = items.findIndex(it => it.id === item.id);
                                                        const isNotOk = item.criteria === "Not Okay";

                                                        return (
                                                            <div
                                                                key={item.id}
                                                                className="bs-card bs-card-light bs-card-sm sieve-item-row"
                                                                style={{ marginBottom: 12, borderLeft: '4px solid #64748b' }}
                                                            >
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
                                                                    <h5 style={{ margin: 0, fontWeight: 600, color: '#334155', fontSize: 13 }}>
                                                                        {item.description}
                                                                    </h5>
                                                                    <div className="toggle-switch-badge">
                                                                        <button
                                                                            type="button"
                                                                            className={`badge badge-lg badge-error ${isNotOk ? 'badge-fill' : ''}`}
                                                                            onClick={() => handleCriteriaChange(realIdx, "Not Okay")}
                                                                        >
                                                                            Not Okay
                                                                        </button>
                                                                        <button
                                                                            type="button"
                                                                            className={`badge badge-lg badge-success ${!isNotOk ? 'badge-fill' : ''}`}
                                                                            onClick={() => handleCriteriaChange(realIdx, "Okay")}
                                                                        >
                                                                            Okay
                                                                        </button>
                                                                    </div>
                                                                </div>

                                                                {isNotOk && (
                                                                    <div className="defect-remarks-field" style={{ display: 'block', marginTop: 10, borderTop: '1px dashed #cbd5e1', paddingTop: 8 }}>
                                                                        <label className="form-label" style={{ fontSize: 11, color: '#475569', marginBottom: 2 }}>
                                                                            Defect Remarks
                                                                        </label>
                                                                        <input
                                                                            type="text"
                                                                            className="form-control check-remarks-input"
                                                                            placeholder="Enter remarks..."
                                                                            style={{ height: 32 }}
                                                                            value={item.defectRemarks || ""}
                                                                            onChange={e => handleRemarksChange(realIdx, e.target.value)}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>

                                                <PhotoAttachField
                                                    photos={photos}
                                                    onChange={setPhotos}
                                                    helperText="Take photo opens the camera directly for this Sieves and Magnets cycle."
                                                />
                                            </div>

                                            {/* Footer Actions */}
                                            <div className="form-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, borderTop: '1px solid #e2e8f0', paddingTop: 15 }}>
                                                <button
                                                    type="button"
                                                    className="bs-btn bs-btn-outline-primary"
                                                    onClick={() => setIsSessionStarted(false)}
                                                >
                                                    Cancel
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={isSubmitting}
                                                    className="bs-btn bs-btn-secondary"
                                                    style={{ backgroundColor: '#64748b', color: '#ffffff', border: 'none' }}
                                                    onClick={() => handleSaveSession(true)}
                                                >
                                                    Pause
                                                </button>
                                                <button
                                                    type="button"
                                                    disabled={isSubmitting}
                                                    className="bs-btn bs-btn-success"
                                                    style={{ backgroundColor: '#10b981', color: '#ffffff', border: 'none', fontWeight: 700, padding: '10px 28px', borderRadius: 8, boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.2)' }}
                                                    onClick={() => handleSaveSession(false)}
                                                >
                                                    <i className="fa fa-check-circle me-1"></i> Complete Tour
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Finish Tour Action */}
                    {state.completedCycles.length > 0 && (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
                            <button
                                type="button"
                                onClick={() => dispatch({ type: 'SET_SCREEN', payload: CCP_SCREENS.SUMMARY })}
                                className="bs-btn bs-btn-primary"
                                style={{ backgroundColor: '#16a34a', borderColor: '#16a34a', padding: '10px 22px', fontSize: 14 }}
                            >
                                View Summary & Finish Tour ({state.completedCycles.length} Cycles Completed)
                            </button>
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default SievesMagnetsChecklist;
